import { CopyIcon, DownloadIcon, ExternalLinkIcon, LinkIcon } from "@chakra-ui/icons";
import { useToast } from "@chakra-ui/react";
import { useContext, useState } from "react";
import { MdOutlineEdit } from "react-icons/md";
import { useIntl } from "react-intl";

import {
  MenuAction,
  MoveDirectoryIcon,
  SchematisationAddIcon,
  TableAction,
  TenantContext,
  TrashIcon,
} from "@nens/ranalib";
import { downloadFile } from "@nens/shared-utils";

import { REVISION_ID_KEY } from "@/config/query-parameter-consts";
import { useRanaSearchState } from "@/hooks/router/rana-search-state";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { useTenantActionAllowed } from "@/hooks/use-action-allowed-in-tenant";
import { useProjectFiles } from "@/hooks/use-project-files";
import { FileDataTypeEnum } from "@/lib/rana-file-data-type";
import { useFileDownloadQueryDirectly } from "@/queries/query-file-download";
import { ProjectContext } from "@/stores/project-provider";
import { fileTypeString } from "@/translations";
import { ProjectFile, ProjectFileOrDirectory, isProjectFile } from "@/types/api-model-simple-names";
import { convertFileIdToQueryParams } from "@/utils/convert-file-id-to-query-params";
import { downloadMultipleFiles } from "@/utils/downloads";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";
import { modelSchemaFileIsFromCatalogue } from "@/utils/model-schema-is-from-catalogue";

import { useMoveProjectFileOrDirectory } from "../api/move";

type ProjectFilesModalType =
  | { type: "import" }
  | {
      type: "delete";
      items: ProjectFileOrDirectory[];
    }
  | {
      type: "move";
      items: ProjectFileOrDirectory[];
    }
  | {
      type: "copy-to";
      items: ProjectFileOrDirectory[];
    }
  | {
      type: "new-folder-with-selection";
      items: ProjectFileOrDirectory[];
    }
  | { type: "duplicate"; item: ProjectFileOrDirectory }
  | { type: "revert-to-version"; fileId: string; ref: string }
  // The confirm move modal requires additional props, those props are all provided by useMoveProjectFileOrDirectory
  // Onclose is omitted because we handle closing the modal in this hook
  | { type: "confirm-move"; moveFiles: () => void }
  | { type: "dont-have-rana-desktop-client" }
  | { type: "submit-schematisation"; item: ProjectFileOrDirectory }
  | null;

function tryOpenRanaProtocol({
  tenantId,
  projectId,
  fileId,
}: {
  tenantId: string;
  projectId: string;
  fileId: string;
}): Promise<boolean> {
  const url = `rana://tenants/${tenantId}/projects/${projectId}/files?path=${fileId}`;
  return new Promise((resolve) => {
    let hasFocus = true;

    const onBlur = () => {
      hasFocus = false;
    };

    window.addEventListener("blur", onBlur);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = url;
    document.body.appendChild(iframe);

    setTimeout(() => {
      window.removeEventListener("blur", onBlur);
      document.body.removeChild(iframe);
      resolve(!hasFocus); // if we lost focus, assume it has the Rana Desktop Client
    }, 1500);
  });
}

function isWindowsPlatform(): boolean {
  if ("userAgentData" in navigator) {
    const userAgentData = navigator.userAgentData as { userAgentData?: { platform: string } };

    if ("platform" in userAgentData && userAgentData?.platform)
      return userAgentData.platform === "Windows";
  }

  return /Win/i.test(navigator.platform || navigator.userAgent);
}

export function useProjectFilesList() {
  const intl = useIntl();
  const { tenant: tenantId } = useContext(TenantContext);
  const project = useContext(ProjectContext);
  const { id: projectId } = project;
  const { canManageDataset, canReadDatasetList } = useTenantActionAllowed();

  const { canWriteProject } = useProjectActionAllowed();
  const fileDownloadQueryDirectly = useFileDownloadQueryDirectly();

  const [viewFileHistory, setViewFileHistory] = useRanaSearchState("history", false);
  const [selectedRevisionId, setSelectedRevisionId] = useRanaSearchState<number | undefined>(
    REVISION_ID_KEY,
    undefined,
    ["number", "undefined"]
  );

  const projectFilesProps = useProjectFiles({
    enableSettingParams: true,
  });
  const { files, path, isLoading, fileRef } = projectFilesProps;

  const toast = useToast();

  const [openModal, setOpenModal] = useState<ProjectFilesModalType>(null);
  const [addDirectoryOpen, setAddDirectoryOpen] = useState<boolean>(false);
  const [remameDirectoryOrFile, setRenameDirectoryOrFile] = useState<ProjectFileOrDirectory | null>(
    null
  );
  // If true dont show a confirm modal when moving a file by drag and drop
  const closeModal = () => setOpenModal(null);
  const [movingTo, setMovingTo] = useState<string[]>([]);
  const moveProjectFileOrDirectoryMutation = useMoveProjectFileOrDirectory(
    (props) => setOpenModal({ type: "confirm-move", ...props }),
    {
      // Set moving to state to show loading in the folder being moved to
      onMutate: (variables) => {
        setMovingTo((alreadyMovingTo) => [...alreadyMovingTo, variables.destination]);
      },
      onSettled: (_, __, variables) => {
        setMovingTo((alreadyMovingTo) =>
          alreadyMovingTo.filter((path) => path !== variables.destination)
        );
      },
    }
  );

  const isMovingInCurrentFolder =
    movingTo?.find((destination) => destination === path) !== undefined;

  const { mutateWithConfirm, dontConfirmMove, setDontConfirmMove } =
    moveProjectFileOrDirectoryMutation;

  const moveFilesByRowIds = async (rowIds: string[], destination: string) => {
    const items = files.filter((file) => rowIds.includes(file.id));
    mutateWithConfirm({
      items,
      destination,
    });
  };

  // Translatable texts
  const downloadText = intl.formatMessage({
    id: "fileaction.download",
    defaultMessage: "Download ",
  });
  const copyText = intl.formatMessage({
    id: "fileaction.copy",
    defaultMessage: "Copy file link",
  });
  const duplicateText = intl.formatMessage({
    id: "fileaction.duplicate",
    defaultMessage: "Duplicate ",
  });
  const moveText = intl.formatMessage({
    id: "fileaction.move",
    defaultMessage: "Move ",
  });
  const copyToText = intl.formatMessage({
    id: "fileaction.copy_to",
    defaultMessage: "Copy to",
  });
  const renameText = intl.formatMessage({
    id: "fileaction.rename",
    defaultMessage: "Rename ",
  });
  const deleteText = intl.formatMessage({
    id: "fileaction.delete",
    defaultMessage: "Delete ",
  });
  const submitSchematisation = intl.formatMessage({
    id: "fileaction.submit_schematisation",
    defaultMessage: "Submit to catalogue",
  });

  function getFileActions(file: ProjectFileOrDirectory): MenuAction[] {
    if (!isProjectFile(file)) return [];

    const typeString = fileTypeString(intl, file.type);
    const fileId = file.id;

    const actions = [];

    if (isWindowsPlatform()) {
      actions.push({
        icon: <ExternalLinkIcon />,
        label: intl.formatMessage({
          id: "fileaction.open_desktop_client",
          defaultMessage: "Open in Rana Desktop Client",
        }),
        action: async () => {
          const success = await tryOpenRanaProtocol({ tenantId, projectId, fileId });
          if (!success) {
            setOpenModal({ type: "dont-have-rana-desktop-client" });
          }
        },
      });
    }

    if (file.dataType !== FileDataTypeEnum.Schematisation) {
      actions.push({
        icon: <DownloadIcon />,
        label: downloadText + typeString,
        action: async () => {
          const { isSuccess, data: downloadUrl } = await fileDownloadQueryDirectly(file);
          if (isSuccess) downloadFile(downloadUrl, getNameFromFileId(fileId));
        },
      });
    }

    actions.push({
      icon: <LinkIcon />,
      label: copyText,
      action: async () => {
        navigator.clipboard.writeText(
          `${window.location.href}&${convertFileIdToQueryParams(fileId)}`
        );
        toast({
          title: intl.formatMessage({
            id: "fileaction.copied_link",
            defaultMessage: "Copied link!",
          }),
        });
      },
    });

    return actions;
  }

  function getWriteActions(fileOrDirectory: ProjectFileOrDirectory): MenuAction[] {
    if (!canWriteProject) return [];

    if (fileRef) return [];
    const typeString = fileTypeString(intl, fileOrDirectory.type);

    const actions = [];

    actions.push(
      {
        icon: <CopyIcon />,
        label: duplicateText + typeString,
        action: () => setOpenModal({ type: "duplicate", item: fileOrDirectory }),
      },
      {
        icon: <MoveDirectoryIcon />,
        label: moveText + typeString,
        action: () => setOpenModal({ type: "move", items: [fileOrDirectory] }),
      }
    );

    actions.push(
      {
        icon: <MdOutlineEdit />,
        label: renameText + typeString,
        action: () => setRenameDirectoryOrFile(fileOrDirectory),
      },
      {
        icon: <TrashIcon />,
        label: deleteText + typeString,
        action: () => setOpenModal({ type: "delete", items: [fileOrDirectory] }),
      }
    );

    return actions;
  }

  function getSchematisationActions(fileOrDirectory: ProjectFileOrDirectory): MenuAction[] {
    // user needs to have the rights to change the dataset on tenant level and the file needs to be a schematisation
    if (!canManageDataset || fileOrDirectory.dataType !== FileDataTypeEnum.Schematisation) {
      return [];
    }

    const actions = [];

    const isFromCatalogue = modelSchemaFileIsFromCatalogue(fileOrDirectory);

    actions.push({
      isDividerBefore: true,
      icon: <SchematisationAddIcon />,
      label: submitSchematisation,
      disabledText: isFromCatalogue
        ? intl.formatMessage({
            id: "fileaction.submit_schematisation.disabled",
            defaultMessage:
              "This model schematisation is from the catalogue and cannot be submitted.",
          })
        : undefined,
      action: () => {
        setOpenModal({ type: "submit-schematisation", item: fileOrDirectory });
      },
    });

    return actions;
  }

  const getActions = (fileOrDirectory: ProjectFileOrDirectory): MenuAction[] => {
    const fileActions = getFileActions(fileOrDirectory);
    const writeActions = getWriteActions(fileOrDirectory);
    const schematisationActions = getSchematisationActions(fileOrDirectory);
    return [...fileActions, ...writeActions, ...schematisationActions];
  };

  const getFiles = (rowIds: string[]) => {
    return files.filter((file) => rowIds.includes(file.id));
  };

  const tableActions: TableAction[] = [
    {
      label: intl.formatMessage({
        id: "button.move",
        defaultMessage: "Move",
      }),
      action: (rowIds) =>
        setOpenModal({
          type: "move",
          items: getFiles(rowIds),
        }),
      icon: <MoveDirectoryIcon />,
    },
    {
      label: copyToText,
      action: (rowIds) =>
        setOpenModal({
          type: "copy-to",
          items: getFiles(rowIds),
        }),
      icon: <MoveDirectoryIcon />,
    },
    {
      label: intl.formatMessage({
        id: "button.new_folder_with_selection",
        defaultMessage: "New folder with selection",
      }),
      action: (rowIds) =>
        setOpenModal({
          type: "new-folder-with-selection",
          items: getFiles(rowIds),
        }),
      icon: <MoveDirectoryIcon />,
    },
    {
      label: intl.formatMessage({
        id: "button.delete",
        defaultMessage: "Delete",
      }),
      action: (rowIds) =>
        setOpenModal({
          type: "delete",
          items: getFiles(rowIds),
        }),
      icon: <TrashIcon />,
    },
    {
      label: intl.formatMessage({
        id: "fileaction.download",
        defaultMessage: "Download",
      }),
      action: async (rowIds) => {
        if (files.some((file) => rowIds.includes(file.id) && file.type === "directory")) {
          toast({
            title: intl.formatMessage({
              id: "projectfileslist.download.directory.warning",
              defaultMessage: "Warning",
            }),
            description: intl.formatMessage({
              id: "projectfileslist.download.directory.warning.explanation",
              defaultMessage:
                "You have one or more folders selected. Please deselect them before trying to download",
            }),
            status: "warning",
            isClosable: true,
          });

          return;
        }

        // only download files, ignore folders
        const filesList = files.filter(
          (file): file is ProjectFile => rowIds.includes(file.id) && file.type === "file"
        );
        downloadMultipleFiles(filesList, fileDownloadQueryDirectly);
      },
      icon: <DownloadIcon />,
    },
  ];

  return {
    ...projectFilesProps,
    isSkeleton: isLoading && !isMovingInCurrentFolder,
    isLoading: isMovingInCurrentFolder,
    moveFilesByRowIds,
    openModal,
    dontConfirmMove,
    setDontConfirmMove,
    addDirectoryOpen,
    setAddDirectoryOpen,
    remameDirectoryOrFile,
    setRenameDirectoryOrFile,
    setOpenModal,
    closeModal,
    getActions,
    moveProjectFileOrDirectoryMutation,
    canWriteProject,
    tableActions,
    project,
    canReadDatasetList,
    canManageDataset,
    viewFileHistory,
    setViewFileHistory,
    selectedRevisionId,
    setSelectedRevisionId,
  } as const;
}
